import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Coins, Trophy, Shuffle, Timer } from "lucide-react";
import { useGameStore } from "@/hooks/useGameStore";
import { MINI_GAMES, MINIGAME_REWARD, UNIQUE_TARGET_IDS } from "@/data/catalog";
import { audio } from "@/game/audio";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Describe the win condition of a mini-game in plain words for the
// Random-pick preview dialog.
function goalSummary(g) {
  switch (g.id) {
    case "boss":         return "Defeat every boss before time runs out.";
    case "trophy_trial": return "You get 3 arrows. Hit the trophy — skulls void your prize.";
    case "hit_em_all":   return `Hit one of each of the ${UNIQUE_TARGET_IDS.length} unique targets. No repeats!`;
    case "skull_dodge":  return `Score ${g.hitGoal} hits. Touch a skull and you lose instantly.`;
    case "wall_breaker": return `Break the walls to hit what's behind them. ${g.hitGoal} targets to clear.`;
    case "target_chain": return "One target. Each hit morphs it. Catch every link in the chain!";
    case "chain_master": return "3 hits per link across the full chain. Survive and win the Chain Crossbow!";
    default:             return `Land ${g.hitGoal} hits before the timer ends.`;
  }
}

export default function MiniGamesScreen() {
  const state = useGameStore();
  const nav = useNavigate();
  const [randomPick, setRandomPick] = useState(null);

  const start = (id) => {
    audio.unlock();
    audio.start();
    nav(`/play/${id}`);
  };

  const pickRandom = () => {
    audio.unlock();
    const pick = MINI_GAMES[Math.floor(Math.random() * MINI_GAMES.length)];
    setRandomPick(pick);
  };

  return (
    <div className="relative w-full h-full arcade-bg overflow-auto" data-testid="minigames-screen">
      <div className="sticky top-0 z-20 backdrop-blur-md bg-[#0B0C10]/60 border-b border-white/5 safe-top">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
          <button className="hud-pill hud-pill--cyan" onClick={() => nav("/")} data-testid="mg-back-btn">
            <ArrowLeft className="w-5 h-5 text-[#00FFFF]" />
            <span className="text-[#00FFFF] font-display text-sm">Home</span>
          </button>
          <h1 className="font-display font-black text-2xl sm:text-3xl neon-cyan">MINI GAMES</h1>
          <div className="hud-pill hud-pill--gold">
            <Coins className="w-4 h-4 text-[#FFD700]" />
            <span className="neon-yellow text-base">{state.coins.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 sm:p-8">
        <p className="text-center text-white/70 font-body mb-6 sm:mb-10">
          Win any mini game for <span className="neon-yellow font-bold">+{MINIGAME_REWARD} coins</span>
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <button
            onClick={pickRandom}
            className="neon-card p-6 text-left group"
            data-testid="minigame-random"
            style={{ borderColor: "#FFD70055" }}
          >
            <div className="flex items-start justify-between">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl font-display font-black"
                style={{
                  background: "#FFD70022",
                  border: "2px solid #FFD700",
                  boxShadow: "0 0 18px #FFD70055",
                  color: "#FFD700",
                }}
              >
                <Shuffle className="w-7 h-7" />
              </div>
            </div>
            <h3 className="font-display font-black text-xl sm:text-2xl mt-4 neon-yellow">
              Random
            </h3>
            <p className="font-body text-white/70 text-sm mt-1">
              Roll the dice — we'll pick a mini game for you.
            </p>
            <div className="mt-4 flex items-center justify-between text-xs font-display uppercase tracking-wider text-white/60">
              <span>Any mode</span>
              <span>{MINI_GAMES.length} to choose from</span>
            </div>
          </button>

          {MINI_GAMES.map((g) => {
            const wins = state.miniGamesWon[g.id] || 0;
            return (
              <button
                key={g.id}
                onClick={() => start(g.id)}
                className="neon-card p-6 text-left group"
                data-testid={`minigame-${g.id}`}
                style={{ borderColor: `${g.color}55` }}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl font-display font-black"
                    style={{
                      background: `${g.color}22`,
                      border: `2px solid ${g.color}`,
                      boxShadow: `0 0 18px ${g.color}55`,
                      color: g.color,
                    }}
                  >
                    {g.icon}
                  </div>
                  {wins > 0 && (
                    <div className="hud-pill hud-pill--gold text-xs">
                      <Trophy className="w-3 h-3 text-[#FFD700]" />
                      <span className="neon-yellow">{wins}</span>
                    </div>
                  )}
                </div>
                <h3 className="font-display font-black text-xl sm:text-2xl mt-4" style={{ color: g.color }}>
                  {g.name}
                </h3>
                <p className="font-body text-white/70 text-sm mt-1">{g.tagline}</p>
                <div className="mt-4 flex items-center justify-between text-xs font-display uppercase tracking-wider text-white/60">
                  <span>{g.duration}s</span>
                  <span>Goal: {g.id === "boss" ? "Defeat boss" : g.id === "trophy_trial" ? "Hit the trophy" : `${g.hitGoal} hits`}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <AlertDialog
        open={!!randomPick}
        onOpenChange={(open) => { if (!open) setRandomPick(null); }}
      >
        <AlertDialogContent
          data-testid="random-pick-dialog"
          className="border-2 shadow-[0_0_24px_rgba(255,215,0,0.35)] bg-[#14082e] text-white"
          style={{
            borderColor: randomPick?.color || "#FFD700",
            boxShadow: `0 0 24px ${randomPick?.color || "#FFD700"}55`,
          }}
        >
          <AlertDialogHeader>
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl font-display font-black mb-2"
              style={{
                background: `${randomPick?.color || "#FFD700"}22`,
                border: `2px solid ${randomPick?.color || "#FFD700"}`,
                boxShadow: `0 0 18px ${randomPick?.color || "#FFD700"}55`,
                color: randomPick?.color || "#FFD700",
              }}
            >
              {randomPick?.icon}
            </div>
            <AlertDialogTitle
              className="font-display font-black text-2xl"
              style={{ color: randomPick?.color || "#FFD700" }}
            >
              {randomPick?.name}
            </AlertDialogTitle>
            <AlertDialogDescription className="font-body text-white/80 space-y-3">
              <span className="block">{randomPick?.tagline}</span>
              <span className="block text-white font-semibold">
                {randomPick ? goalSummary(randomPick) : ""}
              </span>
              <span className="flex items-center gap-2 text-white/70 text-xs font-display uppercase tracking-wider">
                <Timer className="w-3 h-3" />
                {randomPick?.duration}s round
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              data-testid="random-reroll-btn"
              className="btn-neon btn-cyan border-2"
              onClick={(e) => { e.preventDefault(); pickRandom(); }}
            >
              Reroll
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const id = randomPick?.id; setRandomPick(null); if (id) start(id); }}
              data-testid="random-start-btn"
              className="btn-neon btn-pink"
            >
              Let's Go!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
